import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import ErrorResponse from '../../components/ErrorResponse';
import Loader from '../../components/Loader';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import SimpleHeader from '../../components/layout/SimpleHeader';
import { Descriptions } from 'antd';
import Button from '../../components/Button';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/rootReducer';
import { IGetMembers } from '../../models/messages.model';
import {
  createMessage,
  forwardMessage,
  getMembers,
} from '../../api/messages.api';
import SelectMemberModal from '../../components/message/write/modals/SelectMemberModal';
import { CgCloseR } from 'react-icons/cg';
import Tinymce from '../../components/Tinymce';
import FileUpload from '../../components/FileUpload';
import useAuth from '../../hooks/useAuth';

const MessageWrite = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { successNotification, infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetMembers>();
  const {
    data: createRes,
    isLoading: createIsLoading,
    error: createError,
    execute: createExecute,
  } = useAxios();

  const [title, setTitle] = useState('');
  const [memberCodes, setMemberCodes] = useState<string[]>([]);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<any>([null]);
  const [isMessage, setIsMessage] = useState(true);
  const [isSms, setIsSms] = useState(false);
  const [selectMemberVisible, setSelectMemberVisible] = useState(false);
  const [messageCode, setMessageCode] = useState('');
  const [attachFileCode, setAttachFileCode] = useState('');
  const [attachFiles, setAttachFiles] = useState<any>([]);

  const titleInput = useRef<any>();

  useEffect(() => {
    execute(getMembers());

    const _title = sessionStorage.getItem('title');
    const _memberCode = sessionStorage.getItem('memberCode');
    const _content = sessionStorage.getItem('content');
    const _messageCode = sessionStorage.getItem('messageCode');
    const _attachFileCode = sessionStorage.getItem('attachFileCode');
    const _files = sessionStorage.getItem('files');
    console.log('files', _files);
    if (_title) setTitle(_title);
    if (_memberCode) setMemberCodes([_memberCode]);
    if (_content) setContent(_content);
    if (_messageCode) setMessageCode(_messageCode);
    if (_attachFileCode) setAttachFileCode(_attachFileCode);
    if (_files) setAttachFiles(JSON.parse(_files));
    sessionStorage.removeItem('title');
    sessionStorage.removeItem('memberCode');
    sessionStorage.removeItem('content');
    sessionStorage.removeItem('messageCode');
    sessionStorage.removeItem('attachFileCode');
    sessionStorage.removeItem('files');
  }, []);

  useEffect(() => {
    if (createRes && !createIsLoading) {
      successNotification(t('message.write.message1'));
      history.push('/message/send');
    }
  }, [createRes, createIsLoading]);

  const onInit = () => {
    setTitle('');
    setMemberCodes([]);
    setContent('');
    setFiles([null]);
    setIsMessage(true);
    setIsSms(false);
    setSelectMemberVisible(false);
    setMessageCode('');
    setAttachFileCode('');
    setAttachFiles([]);
  };

  const getCampusName = (campusCode: string) => {
    const campus = campusData.find((item) => item.campusCode === campusCode);
    return campus ? campus.campusKorName : '';
  };

  const getMemberName = (memberCode: string) => {
    const member = res?.data.find((item) => item.memberCode === memberCode);
    return member ? member.memKorName : '';
  };

  const onDeleteMember = (memberCode: string) => {
    setMemberCodes(memberCodes.filter((item) => item !== memberCode));
  };

  const onSetFile = (index: number, file: any) => {
    if (file.size > 1024 * 1024 * 20) {
      return infoNotification(t('message.write.message2'));
    }

    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  };

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  };

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  };

  const onSubmit = async () => {
    if (!title) {
      titleInput.current.focus();
      return infoNotification(t('homepage.archive.message1'));
    }
    if (memberCodes.length === 0) {
      return infoNotification(t('message.write.message4'));
    }
    if (!isMessage && !isSms) {
      return infoNotification(t('message.write.message5'));
    }

    const form = new FormData();
    form.append('title', title);
    form.append('content', content);
    memberCodes.forEach((item) => form.append('recipientMemCode', item));

    // 생성 시 파일 처리
    if (files.length > 0) {
      files.forEach((item: any) => {
        if (item !== null) form.append('files', item);
      });
    }

    if (messageCode) {
      form.append('messageCode', messageCode);
      if (attachFileCode) form.append('attachFileCode', attachFileCode);
      createExecute(forwardMessage(Number(messageCode), form));
    } else {
      createExecute(createMessage(form));
    }
  };

  return (
    <div className="content scrollbar">
      {isLoading || createIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={createError} />
      <SimpleHeader
        iconFile="icon-message-big.svg"
        parentMenu={t('common.menu.message')}
        currentMenu={t('message.archive.button1')}
      />

      <div className='comm-table__area rowType edit mt-0'>
        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{ width: '200px' }}
        >
          <Descriptions.Item label={t('common.column.title')}>
            <div className="flex">
              <input
                type="text"
                className="form-control input-text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                ref={titleInput}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('message.archive.column1')}>
            ({getCampusName(auth?.campus[0])}) {auth?.korName}
          </Descriptions.Item>
          <Descriptions.Item label={t('message.smsWrite.column2')}>
            <div className='comm-btn__area'>
            <Button
              className="btn-basic black"
              onClick={() => setSelectMemberVisible(true)}
              text={t('message.smsWrite.button1')}
            />
            </div>
            {memberCodes.map((item) => (
              <>
                <span className="ml-2">{getMemberName(item)}</span>
                <span
                  className="pointer"
                  onClick={() => onDeleteMember(item)}
                >
                  <CgCloseR />
                </span>
              </>
            ))}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.contents')}>
            <Tinymce
              content={content}
              setContent={setContent}
              charLimit={1000}
            />
          </Descriptions.Item>
          {isSms ||
          (messageCode && !attachFiles) ? null : attachFiles.length > 0 ? (
            <Descriptions.Item label={t('common.column.file')}>
              {attachFiles.map((item: any) => (
                <div>
                  <a
                    className="download-link"
                    href={`${item.fileLocation}`}
                    download={item.originalName}
                  >
                    {item.originalName}
                  </a>
                </div>
              ))}
            </Descriptions.Item>
          ) : (
            <Descriptions.Item label={t('common.column.file')}>
              {files.length > 0
                ? files.map((file: any, index: number) => (
                    <div className="comm-gap mb-2" key={index}>
                      <div>
                        <FileUpload
                          file={files[index]}
                          setFile={(file: any) => onSetFile(index, file)}
                        />
                      </div>
                      <div className="comm-btn__area">
                        <Button
                          className="btn-basic small gray"
                          onClick={() => onDeleteFile(index)}
                          text={t('common.button.delete')}
                        />
                      </div>
                    </div>
                  ))
                : null}
              {files.length < 5 ? (
                <div className="comm-btn__area">
                  <Button
                    className="btn-basic pink-line"
                    onClick={onAddFile}
                    text={t('common.button.add')}
                  />
                </div>
              ) : null}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink-line"
            onClick={onInit}
            text={t('common.button.reset')}
          />
          <Button
            className="btn-basic pink-line"
            onClick={onSubmit}
            text={t('message.smsWrite.button2')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {selectMemberVisible && (
        <SelectMemberModal
          visible={selectMemberVisible}
          setVisible={setSelectMemberVisible}
          members={res?.data}
          memberCodes={memberCodes}
          setMemberCodes={setMemberCodes}
        />
      )}
    </div>
  );
};

export default MessageWrite;
